import { useEffect, useRef, useState } from "react";
import { searchPlaces } from "../lib/services";
import { useLang } from "../contexts/LanguageContext";

export default function SearchBox({ onSelect }) {
    const { t } = useLang();
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const ref = useRef(null);
    const timerRef = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    useEffect(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        if (q.trim().length < 3) {
            setResults([]);
            return;
        }
        setLoading(true);
        timerRef.current = setTimeout(async () => {
            try {
                const r = await searchPlaces(q.trim());
                setResults(r);
                setOpen(true);
            } catch {
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 350);
        return () => timerRef.current && clearTimeout(timerRef.current);
    }, [q]);

    const select = (r) => {
        setOpen(false);
        setQ(r.label.split(",")[0]);
        onSelect(r);
    };

    return (
        <div className="relative" ref={ref} data-testid="search-box">
            <div className="flex items-center gap-2 rf-card px-3 py-1.5"
                style={{ background: "var(--bg-card-solid)" }}>
                <i className="fa-solid fa-magnifying-glass text-[var(--text-faint)] text-sm"></i>
                <input
                    type="text"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onFocus={() => results.length > 0 && setOpen(true)}
                    placeholder={t("searchPlace")}
                    className="bg-transparent outline-none text-sm w-[200px] sm:w-[260px] text-[var(--text-primary)]"
                    data-testid="search-input"
                />
                {loading && (
                    <i className="fa-solid fa-spinner fa-spin text-[var(--accent)] text-xs"></i>
                )}
            </div>
            {open && results.length > 0 && (
                <div
                    className="absolute top-full left-0 right-0 mt-1 rf-card z-[600] max-h-[320px] overflow-y-auto fade-up"
                    style={{ background: "var(--bg-card-solid)" }}
                    data-testid="search-results"
                >
                    {results.map((r) => (
                        <button
                            key={r.id}
                            onClick={() => select(r)}
                            className="w-full text-left px-3 py-2 text-xs hover:bg-[var(--accent-soft)] hover:text-[var(--accent)] border-b border-[var(--border-soft)] last:border-b-0 flex items-start gap-2"
                            data-testid={`search-result-${r.id}`}
                        >
                            <i className="fa-solid fa-location-dot text-[var(--accent)] mt-0.5"></i>
                            <span className="line-clamp-2">{r.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
